import type { Participant } from './programs';

export interface CommitteeCycleSummary {
  id: string;
  label: string;
  year: number | null;
  state: string;
  byStatus: Record<string, number>;
}

export interface CommitteeSettings {
  compensation?: { kind: 'honorarium' | 'gift_card' | 'none'; amountCents: number | null };
  externalMembers?: boolean;
  youthDataApproved?: boolean;
  standing?: boolean;
  nameFr?: string | null;
  eligibility?: Record<string, unknown>;
}

export interface Committee {
  id: string;
  key: string;
  name: string;
  description: string | null;
  settings: CommitteeSettings;
  cycles: CommitteeCycleSummary[];
}

export interface EligibilityFlag {
  rule: string;
  severity: 'fail' | 'unknown';
  message: string;
}

export interface CommitteeMember extends Participant {
  perspective: string | null;
  region: string | null;
  language: string | null;
  ageBand: string | null;
  eligibility: { eligible: boolean | null; flags: EligibilityFlag[] } | null;
  meetingsAttended?: number;
  payments?: Record<string, number>;
  restricted?: boolean;
  attendance?: { attended: boolean; source: string; minutes: number | null; recordedAt: string } | null;
}

export interface Meeting {
  id: string;
  title: string;
  startsAt: string | null;
  endsAt: string | null;
  mode: string | null;
  location: string | null;
  briefUrl: string | null;
  minutesUrl: string | null;
  minutesLabel: string | null;
  zoomMeetingId: string | null;
  attended: number | null;
}

export interface CycleDetail {
  program: { id: string; key: string; name: string; description: string | null; settings: CommitteeSettings };
  cycle: { id: string; label: string; year: number | null; state: string };
  roster: CommitteeMember[];
  meetings: Meeting[];
  meetingsHeld: number;
  compensation: Record<string, number>;
  roles: string[];
  perspectives: { key: string; label: string }[];
  ageBands: string[];
}

export interface CountRow {
  value: string;
  count: number;
}

export interface Composition {
  total: number;
  byRole: CountRow[];
  byPerspective: CountRow[];
  byRegion: CountRow[];
  byLanguage: CountRow[];
  equity: {
    answeredBy: number;
    booleans: { key: string; label: string; yes: number; answered: number }[];
    genderIdentity: { items: CountRow[]; total: number };
  };
}

export const AGE_BAND_LABEL: Record<string, string> = { under_18: 'Under 18', '18_29': '18–29', adult: '30 and over' };
export const COMMITTEE_ROLE_LABEL: Record<string, string> = {
  chair: 'Chair',
  co_chair: 'Co-chair',
  member: 'Member',
  observer: 'Observer',
  staff: 'Staff',
};
export const COMPENSATION_LABEL: Record<string, string> = {
  honorarium: 'Honorarium',
  gift_card: 'Gift card',
  none: 'None',
};

export const toIso = (local: string) => (local ? new Date(local).toISOString() : null);
